/**
 * useChangeNavigation — selection + scroll state for the comparison
 * workspace (M3 Phase 3.4).
 *
 * Responsibilities:
 *
 *   1. Track which change is currently "selected" — i.e. the one the
 *      reviewer is operating on with keyboard shortcuts or the minimap.
 *   2. Allow paragraph-level views (`SideBySideView` / `UnifiedView`) to
 *      register their DOM nodes via `register(id, ref)` so navigation
 *      can scroll the right element into view.
 *   3. Provide stable `next()` / `prev()` / `select()` callbacks for the
 *      keyboard hook + minimap to call into.
 *
 * Why a hook (and not redux / context):
 *   - Single consumer (the workspace page).
 *   - Selection state must persist across view-mode toggles (`v` key);
 *     we therefore key on `changeId`, not on row index.
 *
 * Selection seed:
 *   - Initial value = first change's id, or null when there are no
 *     changes. The seed updates whenever the change list reference
 *     fingerprint shifts (different comparison loaded).
 *
 * Scroll behaviour:
 *   - `scrollIntoView({ block: 'center', behavior: 'smooth' })` lands
 *     the change roughly in the viewport's vertical centre, which is
 *     what reviewers expect when they jump with `j`/`k`.
 *   - Browsers without smooth scroll fall back to instant; we don't try
 *     to polyfill — that's outside the M3 scope.
 *
 * Throttle:
 *   - Holding down `j` would otherwise dispatch one selection per
 *     keypress. We trail-throttle by `THROTTLE_MS` so the minimap
 *     cursor doesn't strobe; the user still ends up on the correct
 *     change once they release the key.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ChangeResponse } from '../../lib/veridoc-api';

interface UseChangeNavigationOptions {
  changes: ChangeResponse[];
}

export interface UseChangeNavigationResult {
  /** Currently selected change id (null when there are no changes). */
  selectedChangeId: string | null;
  /** Currently selected zero-based index, or -1 if no selection. */
  selectIndex: number;
  /** Total number of changes (= changes.length). */
  total: number;
  /** Select a change by id and scroll its registered node to viewport centre. */
  select: (changeId: string) => void;
  /** Move selection to the next change (clamped at the last index). */
  next: () => void;
  /** Move selection to the previous change (clamped at index 0). */
  prev: () => void;
  /** Register / unregister a paragraph DOM node for a given change id. */
  register: (changeId: string, element: HTMLElement | null) => void;
  /**
   * Set of change ids whose registered DOM node currently intersects the
   * viewport (any pixel visible). Powers the minimap "what's in view" cue
   * — distinct from `selectedChangeId` (the keyboard / click focus).
   * Empty in environments without `IntersectionObserver` (jsdom, SSR).
   */
  visibleChangeIds: ReadonlySet<string>;
}

/** Trailing throttle for keyboard navigation — keeps selection animation
 *  smooth when the user holds `j`. 50 ms is below the human flicker
 *  threshold but above the React reconciliation cost. */
const THROTTLE_MS = 50;

export function useChangeNavigation({
  changes,
}: UseChangeNavigationOptions): UseChangeNavigationResult {
  const total = changes.length;

  // Stable fingerprint — re-seed selection only when the *set* of changes
  // shifts, not when an unrelated re-render happens. Mirrors the pattern
  // used in `useDecisions`.
  const fingerprint = useMemo(() => {
    if (total === 0) return 'empty';
    const head = changes[0]!.id;
    const tail = changes[total - 1]!.id;
    return `${total}|${head}|${tail}`;
  }, [changes, total]);

  const [selectedChangeId, setSelectedChangeId] = useState<string | null>(
    () => changes[0]?.id ?? null,
  );

  // Re-seed on fingerprint change while preserving the current selection if
  // it still exists in the new list (mostly defensive — refresh keeps ids
  // stable but a server-side recompute could change them).
  useEffect(() => {
    setSelectedChangeId((prev) => {
      if (prev && changes.some((c) => c.id === prev)) return prev;
      return changes[0]?.id ?? null;
    });
    // We deliberately re-key on the fingerprint, not on the live array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint]);

  // DOM node registry — populated by ChangeMarker / DecidableChange via
  // their `register` effect. Held in a ref so registration doesn't
  // invalidate `select`/`next`/`prev` callbacks each render.
  const nodeMap = useRef<Map<string, HTMLElement>>(new Map());

  // Reverse lookup: DOM element → change id. Lets the IntersectionObserver
  // callback resolve the id from `entry.target` without scanning nodeMap.
  const elementToIdRef = useRef<WeakMap<HTMLElement, string>>(new WeakMap());

  // Single shared IntersectionObserver that tracks which change rows are
  // currently in the viewport. Created lazily inside the effect below so
  // SSR / jsdom (no `IntersectionObserver`) cleanly degrades to "no rows
  // visible" without throwing.
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [visibleChangeIds, setVisibleChangeIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    // threshold 0 ⇒ fire as soon as any pixel enters / leaves the viewport.
    const observer = new IntersectionObserver(
      (entries) => {
        // Batch all entries into one state update so the minimap re-renders
        // once per scroll tick, not once per intersecting row.
        setVisibleChangeIds((prev) => {
          const next = new Set(prev);
          let changed = false;
          for (const entry of entries) {
            const id = elementToIdRef.current.get(entry.target as HTMLElement);
            if (!id) continue;
            if (entry.isIntersecting) {
              if (!next.has(id)) {
                next.add(id);
                changed = true;
              }
            } else {
              if (next.delete(id)) changed = true;
            }
          }
          return changed ? next : prev;
        });
      },
      { threshold: 0 },
    );
    observerRef.current = observer;
    // Replay existing nodeMap entries — register() may have been called
    // before this effect ran (mount order varies between strict-mode
    // double-renders).
    for (const [id, el] of nodeMap.current.entries()) {
      elementToIdRef.current.set(el, id);
      observer.observe(el);
    }
    return () => {
      observer.disconnect();
      observerRef.current = null;
      // Reset visible set so a fresh comparison doesn't inherit stale ids.
      setVisibleChangeIds((prev) => (prev.size === 0 ? prev : new Set()));
    };
  }, []);

  const register = useCallback(
    (changeId: string, element: HTMLElement | null): void => {
      if (element) {
        const prev = nodeMap.current.get(changeId);
        if (prev && prev !== element) {
          // Element swapped (e.g. row remounted) — detach the old one first.
          observerRef.current?.unobserve(prev);
          elementToIdRef.current.delete(prev);
        }
        nodeMap.current.set(changeId, element);
        elementToIdRef.current.set(element, changeId);
        observerRef.current?.observe(element);
      } else {
        const prev = nodeMap.current.get(changeId);
        if (prev) {
          observerRef.current?.unobserve(prev);
          elementToIdRef.current.delete(prev);
        }
        nodeMap.current.delete(changeId);
        // Drop the id from the visible set if it was there.
        setVisibleChangeIds((prevSet) => {
          if (!prevSet.has(changeId)) return prevSet;
          const next = new Set(prevSet);
          next.delete(changeId);
          return next;
        });
      }
    },
    [],
  );

  const scrollToChange = useCallback((changeId: string): void => {
    const element = nodeMap.current.get(changeId);
    if (!element) return;
    // Some test environments (jsdom) don't implement scrollIntoView.
    // Guard so unit tests of dependents don't throw.
    if (typeof element.scrollIntoView !== 'function') return;
    element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, []);

  // Trailing throttle: collapse rapid sequential navigation calls into
  // one DOM scroll per `THROTTLE_MS` window. The selection state itself
  // updates synchronously (so the UI feels responsive); only the scroll
  // is debounced.
  const lastScrollAtRef = useRef<number>(0);
  const pendingScrollIdRef = useRef<string | null>(null);
  const pendingScrollTimerRef = useRef<number | null>(null);

  const requestScroll = useCallback(
    (changeId: string): void => {
      const now =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      const elapsed = now - lastScrollAtRef.current;
      if (elapsed >= THROTTLE_MS) {
        lastScrollAtRef.current = now;
        scrollToChange(changeId);
        return;
      }
      // Coalesce — schedule a single trailing scroll.
      pendingScrollIdRef.current = changeId;
      if (pendingScrollTimerRef.current === null) {
        pendingScrollTimerRef.current = window.setTimeout(() => {
          const id = pendingScrollIdRef.current;
          pendingScrollTimerRef.current = null;
          pendingScrollIdRef.current = null;
          if (id !== null) {
            lastScrollAtRef.current =
              typeof performance !== 'undefined'
                ? performance.now()
                : Date.now();
            scrollToChange(id);
          }
        }, THROTTLE_MS - elapsed);
      }
    },
    [scrollToChange],
  );

  // Cleanup any pending throttle timer on unmount.
  useEffect(() => {
    return () => {
      if (pendingScrollTimerRef.current !== null) {
        window.clearTimeout(pendingScrollTimerRef.current);
        pendingScrollTimerRef.current = null;
      }
    };
  }, []);

  const select = useCallback(
    (changeId: string): void => {
      setSelectedChangeId(changeId);
      requestScroll(changeId);
    },
    [requestScroll],
  );

  // Index lookup — O(1) via a memoised id → index Map keyed on the
  // ``changes`` array reference. The map rebuilds only when the array
  // identity changes (re-seed effect); the lookup itself is constant
  // time per render regardless of change list length.
  const idToIndex = useMemo(() => {
    const m = new Map<string, number>();
    for (let i = 0; i < changes.length; i++) {
      m.set(changes[i]!.id, i);
    }
    return m;
  }, [changes]);
  const selectIndex = useMemo(() => {
    if (!selectedChangeId) return -1;
    const idx = idToIndex.get(selectedChangeId);
    return idx === undefined ? -1 : idx;
  }, [idToIndex, selectedChangeId]);

  const next = useCallback((): void => {
    if (total === 0) return;
    const cur = selectIndex < 0 ? -1 : selectIndex;
    const target = Math.min(total - 1, cur + 1);
    const change = changes[target];
    if (!change) return;
    setSelectedChangeId(change.id);
    requestScroll(change.id);
  }, [changes, requestScroll, selectIndex, total]);

  const prev = useCallback((): void => {
    if (total === 0) return;
    const cur = selectIndex < 0 ? 0 : selectIndex;
    const target = Math.max(0, cur - 1);
    const change = changes[target];
    if (!change) return;
    setSelectedChangeId(change.id);
    requestScroll(change.id);
  }, [changes, requestScroll, selectIndex, total]);

  return useMemo(
    () => ({
      selectedChangeId,
      selectIndex,
      total,
      select,
      next,
      prev,
      register,
      visibleChangeIds,
    }),
    [
      selectedChangeId,
      selectIndex,
      total,
      select,
      next,
      prev,
      register,
      visibleChangeIds,
    ],
  );
}
